import React, { useState } from "react";
import {
  Drawer,
  IconButton,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Box,
  AppBar,
  Toolbar,
  Typography,
  Menu,
  MenuItem,
  Divider,
  Tooltip,
} from "@mui/material";
import {
  Home,
  Info,
  Mail,
  ChevronLeft,
  ChevronRight,
  CircleUserRound as AccountCircleIcon,
  PencilRuler,
  Contact,
  BoxIcon,
  NotebookPen,
  LogOut,
  KeyRound,
} from "lucide-react";
import BlogsMenu from "./Pages/BlogsMenu";
import Toolsmenu from "./Pages/Toolsmenu";
import ContactTable from "./Pages/ContactTable";
import ChevronRightIcon from "@mui/icons-material/ChevronRight";
import ChevronLeftIcon from "@mui/icons-material/ChevronLeft";
import Webstats from "./Pages/Webstats";
import { useNavigate } from "react-router-dom";
import ChangePasswordModal from "./Pages/ChangeCreds";

const Sidebar = () => {
  const [isCollapsed, setIsCollapsed] = useState(false);
  const [activeComponent, setActiveComponent] = useState("Blogs");
  const [open, setOpen] = useState(false);
  const navigate = useNavigate();
  const toggleSidebar = () => setIsCollapsed(!isCollapsed);

  const menuItems = [
    { text: "Blogs", icon: <NotebookPen />, element: <BlogsMenu /> },
    { text: "Tools", icon: <PencilRuler />, element: <Toolsmenu /> },
    { text: "Contact Requests", icon: <Contact />, element: <ContactTable /> },
    { text: "Web Content Stats", icon: <BoxIcon />, element: <Webstats /> },
  ];

  const [anchorEl, setAnchorEl] = useState(null);
  const [openMenu, setOpenMenu] = useState(false);

  const handleMenuClick = (event) => {
    setAnchorEl(event.currentTarget);
    setOpenMenu(true);
  };

  const handleOpen = () => setOpen(true);
  const handleClose = () => setOpen(false);

  const handleCloseMenu = () => {
    setOpenMenu(false);
    setAnchorEl(null);
  };

  const handleLogout = () => {
    console.log("Logout clicked");
    handleCloseMenu();
    const keysToRemove = [
      "accesstoken",
      "isAuthenticated",
      "name",
      "refreshtoken",
      "role",
    ];
    keysToRemove.forEach((key) => sessionStorage.removeItem(key));
    navigate("/adminlogin");
  };

  const handleChangeCredentials = () => {
    console.log("Change Credentials clicked");
    handleCloseMenu();
  };

  return (
    <Box
      sx={{
        display: "flex",
        width: "100vw",
        height: "100vh",
        overflowX: "hidden",
      }}
    >
      {/* Left Sidebar */}
      <Drawer
        anchor="left"
        open
        variant="permanent"
        sx={{
          width: isCollapsed ? 60 : 270,
          flexShrink: 0,
          "& .MuiDrawer-paper": {
            width: isCollapsed ? 60 : 270,
            transition: "width 0.3s",
            backgroundColor: "#2e3b4e",
            height: "100vh",
            color: "#fff",
            overflow: "hidden",
            backgroundSize: "cover",
            bgcolor: "#121621",
            backgroundPosition: "center",
            boxSizing: "border-box",
          },
        }}
      >
        <Box
          sx={{
            height: "100%",
            display: "flex",
            flexDirection: "column",
            position: "relative",
            cursor: "pointer",
          }}
          role="presentation"
        >
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
              padding: 2,
              ml: isCollapsed ? 0 : 2,
              borderBottom: "1px solid rgba(255, 255, 255, 0.2)",
              marginBottom: "20px",
            }}
          >
            {/* Dashboard Title */}
            {!isCollapsed && (
              <Typography
                variant="h6"
                sx={{
                  fontWeight: "bold",
                  color: "#fff",
                  letterSpacing: 1,
                }}
              >
                Admin Dashboard
              </Typography>
            )}

            {/* Toggle Button */}
            <IconButton
              onClick={toggleSidebar}
              sx={{
                color: "#fff",
                border: "1px solid #fff",
                borderRadius: "50%",
                padding: 0.5,
                transition: "transform 0.2s",
                "&:hover": {
                  transform: "scale(1.1)",
                },
              }}
            >
              {isCollapsed ? (
                <ChevronRightIcon fontSize="small" />
              ) : (
                <ChevronLeftIcon fontSize="small" />
              )}
            </IconButton>
          </Box>

          {/* Menu Items */}
          <List sx={{ flexGrow: 1, paddingTop: "0px" }}>
            {menuItems.map((item, index) => (
              <Tooltip
                title={isCollapsed ? item.text : ""}
                placement="right"
                key={index}
              >
                <ListItem
                  button
                  key={index}
                  onClick={() => setActiveComponent(item.text)}
                  sx={{
                    backgroundColor:
                      activeComponent === item.text ? "#625bff" : "transparent",
                    py: 2,
                  }}
                >
                  <ListItemIcon sx={{ color: "#fff" }}>
                    {item.icon}
                  </ListItemIcon>
                  {!isCollapsed && <ListItemText primary={item.text} />}
                </ListItem>
              </Tooltip>
            ))}
          </List>
        </Box>
      </Drawer>

      {/* Main Content Area */}
      <Box
        sx={{
          flexGrow: 1,
          overflowX: "hidden",
          display: "flex",
          flexDirection: "column",
        }}
      >
        {/* Top AppBar */}
        <AppBar
          position="static"
          sx={{
            bgcolor: "white",
            color: "black",
            borderBottom: "1px black solid",
            boxSizing: "border-box",
          }}
        >
          <Toolbar>
            <p className="md:text-xl text-lg" sx={{ flexGrow: 1 }}>
              Rohan's Tax Services
            </p>

            {/* Profile Icon with Dropdown Menu */}
            <IconButton
              color="inherit"
              onClick={handleMenuClick}
              sx={{
                position: "absolute",
                top: 10,
                right: 5,
              }}
            >
              <AccountCircleIcon />
            </IconButton>

            <Menu
              anchorEl={anchorEl}
              open={openMenu}
              onClose={handleCloseMenu}
              anchorOrigin={{
                vertical: "top",
                horizontal: "right",
              }}
              transformOrigin={{
                vertical: "top",
                horizontal: "right",
              }}
              sx={{
                marginTop: "30px",
                marginRight: "20px",
              }}
            >
              {/* start from here  */}
              <MenuItem onClick={handleOpen}>
                {<KeyRound size={18} className="mr-2" />}
                Change Credentials
              </MenuItem>
              <ChangePasswordModal open={open} handleClose={handleClose} />
              <MenuItem onClick={handleLogout}>
                {" "}
                {<LogOut size={18} className="mr-2" />}Logout
              </MenuItem>
            </Menu>
          </Toolbar>
        </AppBar>

        <Box
          sx={{
            flexGrow: 1,
            backgroundColor: "#f5f5f5",
            padding: "10px",
            overflowY: "auto",
          }}
        >
          {menuItems.map(
            (item) =>
              activeComponent === item.text && (
                <Box key={item.text} sx={{ width: "100%" }}>
                  {item.element}
                </Box>
              )
          )}
        </Box>
      </Box>
    </Box>
  );
};

export default Sidebar;
